<script lang="ts">
  import { Search, ChevronRight, ChevronDown, File, SlidersHorizontal, X, Check } from 'lucide-svelte'
  import { fileSearchStore, fileSearchStats, type FileSearchResult } from '$lib/stores/fileSearchStoreOptimized'
  import { fileStore } from '$lib/stores/fileStore'
  import { searchStore } from '$lib/stores/searchStore'
  import { searchSettingsStore, updateSearchSettings, createSvelteStore } from '$lib/stores/uiSettingsStore'
  import { get } from 'svelte/store'
  
  let searchInput = $state('')
  let searchInputEl: HTMLInputElement | null = null
  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let searchResults = $derived($fileSearchStore)
  let searchStats = $derived($fileSearchStats)
  let selectedMatch = $state<{fileId: string, matchIndex: number} | null>(null)
  let isComposing = $state(false) // IME composition state
  
  // Search settings - load from persistent store
  const searchSettings = createSvelteStore(searchSettingsStore)
  let showSettings = $state(false)
  let collapseResults = $state(searchSettingsStore.get().collapseResults)
  let showMoreContext = $state(searchSettingsStore.get().showMoreContext)
  let sortBy = $state(searchSettingsStore.get().sortBy)
  let matchCase = $state(searchSettingsStore.get().matchCase)
  let showSortDropdown = $state(false)
  
  // Subscribe to settings changes
  $effect(() => {
    const unsubscribe = searchSettings.subscribe(settings => {
      collapseResults = settings.collapseResults
      showMoreContext = settings.showMoreContext
      sortBy = settings.sortBy
      matchCase = settings.matchCase
    })
    return unsubscribe
  })
  
  // Focus search input when component mounts or when focus-search event is fired
  $effect(() => {
    searchInputEl?.focus()
    
    // Listen for focus-search event
    const handleFocusSearch = () => {
      setTimeout(() => {
        if (searchInputEl) {
          searchInputEl.focus()
          searchInputEl.select()
        }
      }, 100)
    }
    
    window.addEventListener('focus-file-search', handleFocusSearch)
    return () => window.removeEventListener('focus-file-search', handleFocusSearch)
  })
  
  // Close dropdown when clicking outside
  $effect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement
      if (!target.closest('.sort-dropdown-container')) {
        showSortDropdown = false
      }
    }
    
    if (showSortDropdown) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  })
  
  // Debounced search with optimized timing
  function handleSearchInput() {
    // Skip if IME is composing (for Korean, Chinese, Japanese input)
    if (isComposing) {
      return
    }
    
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }
    
    // Clear selection when searching again
    selectedMatch = null
    
    // Show loading state immediately for user feedback
    if (searchInput.length > 0) {
      fileSearchStore.setSearching(true)
    }
    
    // Use shorter debounce for better responsiveness
    const debounceDelay = searchInput.length === 1 ? 100 : (searchInput.length < 3 ? 300 : 200)
    
    searchDebounceTimer = setTimeout(() => {
      fileSearchStore.search(searchInput, matchCase)
      searchDebounceTimer = null
      
      // Apply collapse setting to new results
      if (collapseResults) {
        setTimeout(() => {
          const results = $fileSearchStore.results
          results.forEach(result => {
            if (result.isExpanded) {
              fileSearchStore.toggleExpanded(result.fileId)
            }
          })
        }, 100)
      } else {
        // If collapse is off, expand all results
        setTimeout(() => {
          const results = $fileSearchStore.results
          results.forEach(result => {
            if (!result.isExpanded) {
              fileSearchStore.toggleExpanded(result.fileId)
            }
          })
        }, 100)
      }
    }, debounceDelay)
  }
  
  // Toggle result expansion
  function toggleResult(fileId: string) {
    fileSearchStore.toggleExpanded(fileId)
  }
  
  // Toggle settings panel
  function toggleSettings() {
    showSettings = !showSettings
  }
  
  // Clear search input
  function clearSearch() {
    searchInput = ''
    selectedMatch = null
    isComposing = false
    fileSearchStore.clear()
    // Also clear the search highlighting in the main editor
    searchStore.clear()
    // Hide the search bar in main editor
    searchStore.hide()
    // Dispatch event to clear search term in MainEditor
    window.dispatchEvent(new CustomEvent('clearSearchTerm'))
    searchInputEl?.focus()
  }
  
  // Handle composition events for IME
  function handleCompositionStart() {
    isComposing = true
  }
  
  function handleCompositionEnd() {
    isComposing = false
    // Trigger search after composition ends
    handleSearchInput()
  }
  
  // Open file with search highlighting
  async function openFileWithSearch(fileId: string, searchTerm: string, matchIndex: number, matchInfo?: { lineNumber: number, startIndex: number }) {
    // Set selected match
    selectedMatch = { fileId, matchIndex }
    
    // Open the document
    await fileStore.openFile(fileId)
    
    // Show search bar and set the search term
    searchStore.show()
    searchStore.setSearchTerm(searchTerm)
    
    // Small delay to ensure editor is ready and search is triggered
    setTimeout(() => {
      // Trigger search by dispatching custom event
      // Note: MainEditor now searches in editorText only (without title)
      // so matchInfo positions might need adjustment
      window.dispatchEvent(new CustomEvent('performSearch', { 
        detail: { 
          searchTerm,
          matchInfo 
        }
      }))
    }, 100)
  }
  
  // Get preview text with highlighting
  function getHighlightedText(text: string, start: number, end: number): string {
    const before = text.substring(Math.max(0, start - 30), start)
    const match = text.substring(start, end)
    const after = text.substring(end, Math.min(text.length, end + 30))
    
    return `${before ? '...' : ''}${before}<mark>${match}</mark>${after}${after ? '...' : ''}`
  }
  
  // Escape HTML to prevent XSS
  function escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  // Highlight all occurrences of search term in text
  function highlightText(text: string, searchTerm: string, isMainMatch: boolean = false): string {
    if (!searchTerm || !text) return escapeHtml(text)
    
    // First escape the text to prevent XSS
    const escapedText = escapeHtml(text)
    const escapedSearchTerm = escapeHtml(searchTerm)
    
    const regex = new RegExp(
      `(${escapedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      matchCase ? 'g' : 'gi'
    )
    
    return escapedText.replace(regex, (match) => {
      return isMainMatch 
        ? `<mark class="main-match">${match}</mark>`
        : `<mark class="sub-match">${match}</mark>`
    })
  }
  
  // Format line text - show more context
  function formatLineText(lineText: string, matchStart: number, matchEnd: number): { before: string, match: string, after: string, beforeHtml: string, afterHtml: string } {
    const matchText = lineText.substring(matchStart, matchEnd)
    const contextLength = showMoreContext ? 500 : 150 // Much more context: 500 chars when enabled, 150 chars normally
    
    let beforeText = lineText.substring(Math.max(0, matchStart - contextLength), matchStart)
    let afterText = lineText.substring(matchEnd, Math.min(lineText.length, matchEnd + contextLength))
    
    // Add ellipsis if text was truncated
    const hasEllipsisBefore = matchStart - contextLength > 0
    const hasEllipsisAfter = matchEnd + contextLength < lineText.length
    
    if (hasEllipsisBefore) {
      beforeText = '...' + beforeText
    }
    
    if (hasEllipsisAfter) {
      afterText = afterText + '...'
    }
    
    // Create highlighted HTML versions (excluding main match area)
    const beforeHtml = highlightText(beforeText, searchInput, false)
    const afterHtml = highlightText(afterText, searchInput, false)
    
    return {
      before: beforeText,
      match: matchText,
      after: afterText,
      beforeHtml,
      afterHtml
    }
  }
</script>

<div class="file-search-view">
  <div class="search-header">
    <div class="search-box">
      <div class="search-input-wrapper">
        <Search size={12} class="search-icon" />
        <input
          bind:this={searchInputEl}
          bind:value={searchInput}
          oninput={handleSearchInput}
          oncompositionstart={handleCompositionStart}
          oncompositionend={handleCompositionEnd}
          type="text"
          id="file-search-input"
          aria-label="모든 파일 검색"
          placeholder="모든 파일에서 검색..."
          class="search-input"
        />
        {#if searchInput}
          <button
            class="clear-button"
            onclick={clearSearch}
            title="검색어 지우기"
            aria-label="검색어 지우기"
          >
            <X size={12} />
          </button>
        {/if}
      </div>
      <div class="search-actions">
        <button
          class="settings-button"
          class:active={showSettings}
          onclick={toggleSettings}
          title="검색 설정"
          aria-label="검색 설정"
          aria-expanded={showSettings}
        >
          <SlidersHorizontal size={14} />
        </button>
      </div>
    </div>
    
    {#if showSettings}
      <div class="settings-panel">
        <div class="setting-item">
          <span class="setting-label">검색 결과 접기</span>
          <label class="toggle-switch">
            <input
              type="checkbox"
              bind:checked={collapseResults}
              onchange={() => {
                updateSearchSettings({ collapseResults })
                // Apply collapse/expand to all current results immediately
                if (searchResults.results) {
                  searchResults.results.forEach(result => {
                    if (collapseResults && result.isExpanded) {
                      fileSearchStore.toggleExpanded(result.fileId)
                    } else if (!collapseResults && !result.isExpanded) {
                      fileSearchStore.toggleExpanded(result.fileId)
                    }
                  })
                }
              }}
            />
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="setting-item">
          <span class="setting-label">더 많은 컨텍스트</span>
          <label class="toggle-switch">
            <input
              type="checkbox"
              bind:checked={showMoreContext}
              onchange={() => {
                updateSearchSettings({ showMoreContext })
              }}
            />
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="setting-item">
          <span class="setting-label">대소문자 구분</span>
          <label class="toggle-switch">
            <input
              type="checkbox"
              bind:checked={matchCase}
              onchange={() => {
                updateSearchSettings({ matchCase })
                if (searchInput) {
                  fileSearchStore.search(searchInput, matchCase)
                }
              }}
            />
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="setting-item">
          <span class="setting-label">정렬 기준</span>
          <div class="sort-dropdown-container">
            <button
              class="sort-dropdown-trigger"
              onclick={() => showSortDropdown = !showSortDropdown}
            >
              <span>
                {#if sortBy === 'name-asc'}
                  파일 이름 (A to Z)
                {:else if sortBy === 'name-desc'}
                  파일 이름 (Z to A)
                {:else if sortBy === 'modified-new'}
                  수정 시간 (최신순)
                {:else if sortBy === 'modified-old'}
                  수정 시간 (오래된순)
                {:else if sortBy === 'created-new'}
                  생성 시간 (최신순)
                {:else if sortBy === 'created-old'}
                  생성 시간 (오래된순)
                {/if}
              </span>
              <ChevronDown size={10} />
            </button>
            {#if showSortDropdown}
              <div class="sort-dropdown-menu">
                <button
                  class="sort-option"
                  onclick={() => { sortBy = 'name-asc'; updateSearchSettings({ sortBy }); showSortDropdown = false }}
                >
                  {#if sortBy === 'name-asc'}<Check size={12} />{/if}
                  <span>파일 이름 (A to Z)</span>
                </button>
                <button
                  class="sort-option"
                  onclick={() => { sortBy = 'name-desc'; updateSearchSettings({ sortBy }); showSortDropdown = false }}
                >
                  {#if sortBy === 'name-desc'}<Check size={12} />{/if}
                  <span>파일 이름 (Z to A)</span>
                </button>
                <button
                  class="sort-option"
                  onclick={() => { sortBy = 'modified-new'; updateSearchSettings({ sortBy }); showSortDropdown = false }}
                >
                  {#if sortBy === 'modified-new'}<Check size={12} />{/if}
                  <span>수정 시간 (최신순)</span>
                </button>
                <button
                  class="sort-option"
                  onclick={() => { sortBy = 'modified-old'; updateSearchSettings({ sortBy }); showSortDropdown = false }}
                >
                  {#if sortBy === 'modified-old'}<Check size={12} />{/if}
                  <span>수정 시간 (오래된순)</span>
                </button>
                <button
                  class="sort-option"
                  onclick={() => { sortBy = 'created-new'; updateSearchSettings({ sortBy }); showSortDropdown = false }}
                >
                  {#if sortBy === 'created-new'}<Check size={12} />{/if}
                  <span>생성 시간 (최신순)</span>
                </button>
                <button
                  class="sort-option"
                  onclick={() => { sortBy = 'created-old'; updateSearchSettings({ sortBy }); showSortDropdown = false }}
                >
                  {#if sortBy === 'created-old'}<Check size={12} />{/if}
                  <span>생성 시간 (오래된순)</span>
                </button>
              </div>
            {/if}
          </div>
        </div>
      </div>
    {/if}
    
    {#if searchStats.hasResults}
      <div class="search-stats">
        {searchStats.fileCount}개 파일에서 {searchStats.totalMatches}개 결과
      </div>
    {/if}
  </div>
  
  <div class="search-results">
    {#if searchInput && !searchResults.isSearching && !searchStats.hasResults}
      <div class="no-results">
        <Search size={28} />
        <p>"{searchInput}"에 대한 검색 결과가 없습니다</p>
      </div>
    {:else if searchInput && searchResults.isSearching}
      <div class="searching">
        <p>검색 중...</p>
      </div>
    {:else if searchStats.hasResults}
      {@const sortedResults = (() => {
        const results = [...searchResults.results]
        switch (sortBy) {
          case 'name-asc':
            return results.sort((a, b) => a.fileName.localeCompare(b.fileName, 'ko'))
          case 'name-desc':
            return results.sort((a, b) => b.fileName.localeCompare(a.fileName, 'ko'))
          case 'modified-new':
            return results.sort((a, b) => {
              if (!a.lastModified && !b.lastModified) return 0
              if (!a.lastModified) return 1
              if (!b.lastModified) return -1
              return b.lastModified.getTime() - a.lastModified.getTime()
            })
          case 'modified-old':
            return results.sort((a, b) => {
              if (!a.lastModified && !b.lastModified) return 0
              if (!a.lastModified) return 1
              if (!b.lastModified) return -1
              return a.lastModified.getTime() - b.lastModified.getTime()
            })
          case 'created-new':
            return results.sort((a, b) => {
              if (!a.createdAt && !b.createdAt) return 0
              if (!a.createdAt) return 1
              if (!b.createdAt) return -1
              return b.createdAt.getTime() - a.createdAt.getTime()
            })
          case 'created-old':
            return results.sort((a, b) => {
              if (!a.createdAt && !b.createdAt) return 0
              if (!a.createdAt) return 1
              if (!b.createdAt) return -1
              return a.createdAt.getTime() - b.createdAt.getTime()
            })
          default:
            return results
        }
      })()}
      {#each sortedResults as result (result.fileId)}
        <div class="search-result-file">
          <button
            class="result-header"
            onclick={() => toggleResult(result.fileId)}
          >
            <span class="result-toggle">
              {#if result.isExpanded}
                <ChevronDown size={12} />
              {:else}
                <ChevronRight size={12} />
              {/if}
            </span>
            <span class="result-filename">{result.fileName}</span>
            <span class="result-count">{result.matches.length}</span>
          </button>
          
          {#if result.isExpanded}
            <div class="result-matches">
              {#each result.matches as match, matchIndex}
                {@const formatted = formatLineText(match.lineText, match.startIndex, match.endIndex)}
                <button
                  class="match-item"
                  class:selected={selectedMatch?.fileId === result.fileId && selectedMatch?.matchIndex === matchIndex}
                  onclick={() => openFileWithSearch(result.fileId, searchInput, matchIndex, { 
                    lineNumber: match.lineNumber, 
                    startIndex: match.startIndex 
                  })}
                >
                  <span class="match-text">
                    <span class="before">{@html formatted.beforeHtml}</span>
                    <mark class="main-match">{@html escapeHtml(formatted.match)}</mark>
                    <span class="after">{@html formatted.afterHtml}</span>
                  </span>
                </button>
              {/each}
            </div>
          {/if}
        </div>
      {/each}
    {:else if !searchInput}
      <div class="search-empty">
        <Search size={32} />
        <p>검색어를 입력하세요</p>
        <p class="search-tip">모든 파일의 내용을 검색합니다</p>
      </div>
    {/if}
  </div>
</div>

<style>
  .file-search-view {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: transparent; /* Use parent's background */
  }
  
  .search-header {
    padding: 6px;
    border-bottom: 0.5px solid var(--border-primary);
    flex-shrink: 0;
    background: transparent;
  }
  
  .search-box {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .search-input-wrapper {
    flex: 1;
    display: flex;
    align-items: center;
    padding: 3px 6px;
    padding-left: 22px; /* Make room for icon */
    background: var(--bg-secondary);
    border: 0.5px solid var(--border-secondary);
    border-radius: 3px;
    position: relative;
    min-width: 0; /* Prevent flex item from overflowing */
  }
  
  .search-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  :global(.search-icon) {
    position: absolute;
    left: 6px;
    color: var(--text-tertiary);
    width: 12px;
    height: 12px;
    opacity: 0.6;
    pointer-events: none;
  }
  
  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: var(--ui-font-size-sm); /* 11px */
    color: var(--text-primary);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    min-width: 0; /* Fix for text not showing in flex container */
    letter-spacing: -0.01em;
    padding-left: 20px; /* Make room for icon */
  }
  
  .search-input::placeholder {
    color: var(--text-tertiary);
    opacity: 0.5;
  }
  
  .clear-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1px;
    background: transparent;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.1s ease;
    color: var(--text-tertiary);
    opacity: 0.5;
  }
  
  .clear-button:hover {
    opacity: 1;
    color: var(--text-primary);
  }
  
  .settings-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    background: transparent;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.1s ease;
    color: var(--text-tertiary);
    opacity: 0.7;
  }
  
  .settings-button:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  .settings-button.active {
    background: var(--bg-tertiary);
    color: var(--accent-primary);
  }
  
  .settings-panel {
    position: relative;
    margin-top: 4px;
    padding: 6px 8px;
    background: var(--bg-secondary);
    border: 0.5px solid var(--border-secondary);
    border-radius: 4px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }
  
  .setting-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: var(--ui-font-size-sm); /* 11px */
    color: var(--text-primary);
    opacity: 0.9;
  }
  
  .setting-label {
    user-select: none;
    font-weight: 400;
    letter-spacing: -0.01em;
  }
  
  /* Toggle Switch Styles */
  .toggle-switch {
    position: relative;
    width: 28px;
    height: 16px;
    display: inline-block;
  }
  
  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--bg-tertiary);
    border: 0.5px solid var(--border-secondary);
    transition: 0.2s;
    border-radius: 16px;
  }
  
  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 11px;
    width: 11px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.2s;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }
  
  .toggle-switch input:checked + .toggle-slider {
    background-color: var(--accent-primary);
    border-color: var(--accent-primary);
    opacity: 0.9;
  }
  
  .toggle-switch input:checked + .toggle-slider:before {
    transform: translateX(12px);
  }
  
  .toggle-switch:hover .toggle-slider {
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.1);
    opacity: 0.95;
  }
  
  .search-stats {
    margin-top: 2px;
    font-size: var(--ui-font-size-xs); /* 10px */
    color: var(--text-tertiary);
    padding: 0 2px;
    opacity: 0.6;
    letter-spacing: -0.01em;
  }
  
  .search-results {
    flex: 1;
    overflow-y: auto;
    padding: 0;
  }
  
  .search-result-file {
    margin-bottom: 0;
    background: transparent;
    border: none;
    border-radius: 0;
    overflow: hidden;
    position: relative;
  }
  
  .search-result-file:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 8px;
    right: 8px;
    height: 0.5px;
    background: var(--border-primary);
    opacity: 0.15;
  }
  
  .result-header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 8px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.1s ease;
    text-align: left;
    font-size: var(--ui-font-size-md); /* 13px - 타이틀은 더 크게 */
    color: var(--text-primary);
  }
  
  .result-header:hover {
    background: var(--bg-tertiary);
    opacity: 0.95;
  }
  
  .result-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    height: 10px;
    color: var(--text-tertiary);
    opacity: 0.5;
    transition: opacity 0.1s ease;
  }
  
  .result-filename {
    flex: 1;
    font-weight: 500; /* 타이틀은 약간 더 굵게 */
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.2;
    letter-spacing: -0.01em;
  }
  
  .result-count {
    padding: 1px 5px;
    background: var(--accent-primary);
    opacity: 0.7;
    border-radius: 8px;
    font-size: var(--ui-font-size-sm); /* 11px - 카운트도 약간 크게 */
    color: white;
    font-weight: 500;
    min-width: 20px;
    text-align: center;
  }
  
  .result-matches {
    border-top: none;
    max-height: none; /* Remove height limit to show all results */
    overflow-y: auto;
    background: transparent;
    margin-left: 18px; /* 타이틀에 속해있다는 것을 보여주기 위한 들여쓰기 */
    border-radius: 0;
    margin-top: 0;
    margin-bottom: 4px;
    padding-left: 8px; /* 수직선과의 간격 */
    border-left: 1px solid var(--border-secondary); /* subtle한 수직선 */
    position: relative;
  }
  
  .match-item {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 4px 12px 4px 8px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.1s ease;
    text-align: left;
    font-size: 12px; /* 12px - 검색 내용은 타이틀보다 작게 */
    color: var(--text-primary);
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    border-left: 2px solid transparent;
    position: relative;
    margin-left: -2px; /* 호버시 보더가 제대로 보이도록 */
  }
  
  .match-item:hover {
    background: var(--bg-tertiary);
    border-left-color: var(--accent-primary);
  }
  
  .match-item.selected {
    background: var(--bg-tertiary);
    border-left-color: var(--accent-primary);
    opacity: 1;
  }
  
  .match-item.selected::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--accent-primary);
    opacity: 0.05;
    pointer-events: none;
  }
  
  .match-item.selected:hover {
    background: var(--bg-tertiary);
    border-left-color: var(--accent-primary);
  }
  
  .match-item:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 8px;
    right: 10px;
    height: 0.5px;
    background: var(--border-primary);
    opacity: 0.15;
  }
  
  /* Removed match-line-number styles since we're not using line numbers anymore */
  
  .match-text {
    flex: 1;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px; /* 12px - 타이틀보다 작게 */
    letter-spacing: -0.01em;
  }
  
  /* Main match - the primary search result */
  .match-text :global(.main-match) {
    background: rgba(255, 152, 0, 0.5);
    color: inherit;
    padding: 1px 2px;
    margin: 0;
    border-radius: 2px;
    font-weight: 600;
    display: inline;
    border: none;
    letter-spacing: normal;
    word-spacing: normal;
    box-shadow: 0 0 0 1px rgba(255, 152, 0, 0.2);
    transition: all 0.15s ease;
  }
  
  /* Hover effect for main match */
  .match-item:hover .match-text :global(.main-match) {
    background: rgba(255, 152, 0, 0.65);
    box-shadow: 0 0 0 1px rgba(255, 152, 0, 0.3);
  }
  
  /* Selected match item enhancement */
  .match-item.selected .match-text :global(.main-match) {
    background: rgba(255, 152, 0, 0.7);
    box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.3);
  }
  
  /* Sub matches - additional occurrences in context */
  .match-text :global(.sub-match) {
    background: rgba(255, 235, 59, 0.3);
    color: inherit;
    padding: 0 1px;
    margin: 0;
    border-radius: 1px;
    font-weight: 500;
    display: inline;
    border: none;
    letter-spacing: normal;
    word-spacing: normal;
  }
  
  /* Fallback for old mark tags */
  .match-text mark {
    background: rgba(255, 235, 59, 0.35);
    color: inherit;
    padding: 0 1px;
    margin: 0;
    border-radius: 1px;
    font-weight: 500;
    display: inline;
    border: none;
    letter-spacing: normal;
    word-spacing: normal;
  }
  
  .match-text .before,
  .match-text .after {
    opacity: 0.8;
    display: inline;
  }
  
  
  .no-results,
  .searching,
  .search-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 12px;
    text-align: center;
    color: var(--text-tertiary);
  }
  
  .no-results p,
  .search-empty p {
    margin-top: 6px;
    font-size: var(--ui-font-size-sm); /* 11px */
    opacity: 0.8;
  }
  
  .search-tip {
    margin-top: 3px !important;
    font-size: var(--ui-font-size-xs) !important; /* 10px */
    opacity: 0.5;
  }
  
  .sort-dropdown-container {
    position: relative;
    z-index: 1000;
  }
  
  .sort-dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2px;
    padding: 2px 4px;
    background: transparent;
    border: none;
    border-radius: 3px;
    font-size: var(--ui-font-size-sm); /* 11px */
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
    min-width: 110px;
    text-align: left;
  }
  
  .sort-dropdown-trigger:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  .sort-dropdown-menu {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    min-width: 160px;
    padding: 2px;
  }
  
  :global(.dark-theme) .sort-dropdown-menu {
    background: #1a1a1a !important;
  }
  
  .sort-option {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    padding: 3px 5px;
    background: transparent;
    border: none;
    border-radius: 2px;
    font-size: var(--ui-font-size-sm); /* 11px */
    color: var(--text-primary);
    cursor: pointer;
    transition: background 0.15s ease;
    text-align: left;
  }
  
  .sort-option:hover {
    background: var(--bg-tertiary);
  }
  
  .sort-option :global(svg) {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    color: var(--accent-primary);
  }
  
  .sort-option span {
    flex: 1;
  }
  
  .sort-option:not(:has(svg)) {
    padding-left: 16px;
  }
  
  /* Dark mode adjustments */
  :global(.dark-theme) .match-text :global(.main-match) {
    background: rgba(255, 152, 0, 0.4);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.7);
  }
  
  :global(.dark-theme) .match-text :global(.sub-match) {
    background: rgba(255, 235, 59, 0.2);
  }
  
  /* Dark mode selected match */
  :global(.dark-theme) .match-item.selected .match-text :global(.main-match) {
    background: rgba(255, 152, 0, 0.6);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
  }
  
  :global(.dark-theme) .match-text mark {
    background: rgba(255, 235, 59, 0.25);
  }
  
  :global(.dark-theme) .result-matches {
    border-left-color: var(--border-primary);
    opacity: 0.5;
  }
  
  :global(.dark-theme) .toggle-slider:before {
    background-color: var(--bg-primary);
  }
  
  :global(.dark-theme) .settings-panel {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  }
  
</style>
